import { RootState } from '@next/core-lib/app';
import { DataSeries, DataSeriesParameter } from '@next/core-lib/models';

export interface PerformanceModuleState extends RootState {
  selectedCycleId?: number;
  selectedWeekNr: number;
  selectedSectionIds: number[];
  selectedKpiParameters: Parameter[];
  observations: ObservationsRegistration[];
  weeklyKpiDataResults: WeeklyKpiDataResult[];
  dailyKpiTimeSeries: DataSeries<number, number>;
}

export interface ObservationsRegistration {
  cycleId: number;
  barnId: number;
  sectionId: number;
  day: number;
  animalWeightInGrams?: number;
  uniformity?: number;
  coefficientOfVariation?: number;
  feedName?: string;
  feedIntakeInGramsPerAnimal?: number;
  mortalityRegistration?: MortalityRegistration;
  waterIntakeInLitersTotal?: number;
  numberOfMinutesLight?: number;
  lightIntensityInLux?: number;
  notes?: string;
}

export interface MortalityRegistration {
  death?: number;
  cripple?: number;
  sexError?: number;
  selection?: number;
  other?: number;
  damages?: number;
}

export interface GetObservationsResponse {
  observations: ObservationsRegistration[];
}

export enum TabMark {
  Finished,
  PendingChanges,
  Clean,
}

export enum Parameter {
  AnimalWeightInGrams = 'AnimalWeightInGrams',
  GrowthComparedToReference = 'GrowthComparedToReference',
  MortalityPercentageAll = 'MortalityPercentageAll',
  MortalityPercentageDeath = 'MortalityPercentageDeath',
  MortalityPercentageCripple = 'MortalityPercentageCripple',
  MortalityPercentageSexError = 'MortalityPercentageSexError',
  MortalityPercentageSelection = 'MortalityPercentageSelection',
  MortalityPercentageOther = 'MortalityPercentageOther',
  MortalityPercentageDamages = 'MortalityPercentageDamages',
  CumulativeMortalityAmount = 'CumulativeMortalityAmount',
  CumulativeMortalityPercentage = 'CumulativeMortalityPercentage',
  NumberOfAnimalsAlive = 'NumberOfAnimalsAlive',
  FeedIntakeInGramsPerAnimalPerDay = 'FeedIntakeInGramsPerAnimalPerDay',
  FeedName = 'FeedName',
  WaterIntakeInMililitersPerAnimalPerDay = 'WaterIntakeInMililitersPerAnimalPerDay',
  WaterFeedRatio = 'WaterFeedRatio',
  NumberOfHoursLight = 'NumberOfHoursLight',
  LightIntensityInLux = 'LightIntensityInLux',
  Uniformity = 'Uniformity',
  Notes = 'Notes',
  FeedIntakeNormReference = 'FeedIntakeNormReference',
  AnimalWeightNormReference = 'AnimalWeightNormReference',
  ArrivalAgeInDays = 'ArrivalAgeInDays',
}

export const availableKpiParameters: Parameter[] = [
  Parameter.AnimalWeightInGrams,
  Parameter.GrowthComparedToReference,
  Parameter.MortalityPercentageAll,
  Parameter.MortalityPercentageDeath,
  Parameter.MortalityPercentageCripple,
  Parameter.MortalityPercentageSexError,
  Parameter.MortalityPercentageSelection,
  Parameter.MortalityPercentageOther,
  Parameter.MortalityPercentageDamages,
  Parameter.CumulativeMortalityAmount,
  Parameter.CumulativeMortalityPercentage,
  Parameter.NumberOfAnimalsAlive,
  Parameter.FeedIntakeInGramsPerAnimalPerDay,
  Parameter.FeedName,
  Parameter.WaterIntakeInMililitersPerAnimalPerDay,
  Parameter.WaterFeedRatio,
  Parameter.NumberOfHoursLight,
  Parameter.LightIntensityInLux,
  Parameter.Notes,
];

export const chartCapableKpis: Parameter[] = [
  Parameter.AnimalWeightInGrams,
  Parameter.GrowthComparedToReference,
  Parameter.MortalityPercentageAll,
  Parameter.MortalityPercentageDeath,
  Parameter.MortalityPercentageCripple,
  Parameter.MortalityPercentageSexError,
  Parameter.MortalityPercentageSelection,
  Parameter.MortalityPercentageOther,
  Parameter.MortalityPercentageDamages,
  Parameter.CumulativeMortalityAmount,
  Parameter.CumulativeMortalityPercentage,
  Parameter.NumberOfAnimalsAlive,
  Parameter.FeedIntakeInGramsPerAnimalPerDay,
  Parameter.WaterIntakeInMililitersPerAnimalPerDay,
  Parameter.WaterFeedRatio,
  Parameter.NumberOfHoursLight,
  Parameter.LightIntensityInLux,
];

export interface GetWeeklyKpiDataRequest {
  parameters: Parameter[];
  sectionIds: number[];
}

export interface WeeklyKpiDataResponse {
  results: WeeklyKpiDataResult[];
}

export interface WeeklyKpiDataResult {
  cycleId: number;
  sectionId: number;
  weekNr: number;
  data: KpiValue[];
}

export interface KpiValue {
  parameter: DataSeriesParameter;
  value: number;
}

export const SettingsPoultryNEXTRearingContextKey = 'PoultryNEXTRearing';
export const DeselectedKpisKey = 'DeselectedKpis';
